import React from 'react'
import { FormRenderProps } from 'react-final-form'
import checkPermission from '../../../../../utilities/checkPermission'
import * as defineTypeFormTypes from './defineProjectType'
import useAppFolderAndTabAction from '../../../../../stores/appFolderAndTab/appFolderAndTabAction'
import _ from 'lodash'
import { RoutePath } from '../../../../../router/routesConfig'
import { useHistory } from 'react-router-dom'
import { DialogType } from '../../../../../control/dialog/dialog'
import useLoading from '../../../../../control/loading/useLoading'
import * as REQServiceType from '../../../../../services/web/types/requestWebServiceTypes'
import useAuthenStore from '../../../../../stores/authen/authenStore'
import useTranslate from '../../../../../i18n/useTranslate'
import useWebService from '../../../../../services/web/useWebService'
import useFolderAndTab from '../../useFolderAndTab'
import constConsiderType from '../../../../../constants/considerType'
import constCommitteeProjectRole from '../../../../../constants/committeeProjectRole'
import constSubmittedStep from '../../../../../constants/submittedStep'

const useDefineProject = (props: defineTypeFormTypes.UseDefineTypeFormTypesType) => {
    const { projectData, onSaveComplete } = props

    const callWebService = useWebService()
    const checkPermissionHook = checkPermission()
    const tabAndFolderHook = useFolderAndTab()
    const history = useHistory()
    const translate = useTranslate()
    const loadingHook = useLoading()
    const appFolderAndTabAction = useAppFolderAndTabAction()
    const authenStore = useAuthenStore()

    const [initialValues, setInitialValues] = React.useState<any>({})
    const [required, setRequired] = React.useState<boolean>(true)

    const isViewMode = checkPermissionHook.chkProjectStepViewMode(projectData)
    const isShowUpdateDate = checkPermissionHook.chkShowUpdateDate(projectData)

    const [configDialog, setConfigDialog] =
        React.useState<Pick<DialogType, 'title' | 'content' | 'type' | 'onClickConfirm' | 'onAfterClose'>>()
    const dialogRef = React.useRef<Record<string, Function>>()
    const formRef = React.useRef<FormRenderProps>()
    const isEditDirector = React.useRef<boolean>(false)

    React.useEffect(() => {
        if (projectData) {
            createInitialData()
            isEditDirector.current =
                !checkPermissionHook.isUserCommittee() &&
                projectData?.stepID > constSubmittedStep.specifyProjectType &&
                projectData?.stepID < constSubmittedStep.synthesis &&
                projectData?.committeeInfo.length > 0
        }

        if (projectData?.considerInfo && projectData?.considerInfo?.value) {
            setRequired(projectData?.considerInfo?.value === constConsiderType.exempt ? false : true)
        }
    }, [projectData?.projectID ?? null, projectData?.submittedID ?? null, projectData?.considerInfo ?? null])

    const onRefDialog = (func: Record<string, Function>) => {
        //ห้าม set State หรือเรียก Action ในนี้
        dialogRef.current = func
    }

    const createInitialData = () => {
        const committeeInfo = projectData?.committeeInfo?.map((item: any, index: number) => {
            return {
                director:
                    item.fullUsername_th && item.userID
                        ? { label: item.fullUsername_th, value: item.userID.toString() }
                        : {},
                role:
                    item.projectRoleInfo && item.projectRoleInfo.nameTH && item.projectRoleInfo.value
                        ? { label: item.projectRoleInfo.nameTH, value: item.projectRoleInfo.value.toString() }
                        : {},
            }
        })

        const initialData = {
            isNotAboutPeople: projectData?.isNotAboutPeople ?? false,
            projectType:
                projectData?.considerInfo && projectData?.considerInfo.value
                    ? {
                          label: projectData?.considerInfo.nameTH,
                          value: projectData?.considerInfo.value.toString(),
                      }
                    : null,
            // announceDate: projectData?.announceDate,
            comment: projectData?.reviewerRemark ? projectData?.reviewerRemark : null,
            commentWithFormat: projectData?.reviewerRemarkWithFormat,
            committeeInfo: committeeInfo && committeeInfo.length > 0 ? committeeInfo : [{ director: null, role: null }],
            reviewer: projectData?.reviewerUserID
                ? {
                      label: `${projectData?.reviewerName_th}`,
                      value: `${projectData?.reviewerUserID}`,
                  }
                : null,
        }

        setInitialValues(initialData)
    }

    const getSaveServiceParameter = (isDraft: boolean) => {
        const value = formRef.current?.values

        let param: REQServiceType.SpecifyProjectTypeRequestType = {
            loginUserID: null,
            projectID: null,
            submittedID: null,
            isNotAboutPeople: false,
            considerTypeID: null,
            reviewerRemark: null,
            reviewerRemarkHTML: null,
            committeeInfo: [],
            reviewerUserID: null,
            isDraft: null,
        }

        if (value?.isNotAboutPeople) {
            //ไม่เกี่ยวกับคน
            param = {
                loginUserID: authenStore.profile.userId,
                projectID: projectData.projectID,
                submittedID: projectData.submittedID,
                isNotAboutPeople: value.isNotAboutPeople,
                considerTypeID: null,
                reviewerRemark: value?.commentWithFormat?.content ?? null,
                reviewerRemarkHTML: value?.commentWithFormat?.contentHtml ?? null,
                committeeInfo: [],
                reviewerUserID: null,
                isDraft: isDraft,
            }
        } else {
            const committeeInfo: Array<REQServiceType.SpecifyProjectTypeCommitteeInfo> = value?.committeeInfo?.map(
                (item: { director: defineTypeFormTypes.OptType; role: defineTypeFormTypes.OptType }) => {
                    return {
                        committeeID: item?.director?.value ? parseInt(item?.director.value) : null,
                        projectRoleID: item?.role?.value ? parseInt(item?.role.value) : null,
                    }
                },
            )

            let considerTypeID = null
            if (value?.projectType?.value) {
                considerTypeID = parseInt(value.projectType.value)
            }

            let reviewerUserID = authenStore.profile.userId
            if (checkPermissionHook.isUserAdmin()) {
                reviewerUserID = null
                if (value?.reviewer?.value) {
                    reviewerUserID = parseInt(value.reviewer.value)
                }
            }
            param = {
                loginUserID: authenStore.profile.userId,
                projectID: projectData.projectID,
                submittedID: projectData.submittedID,
                considerTypeID: considerTypeID,
                reviewerRemark: value?.commentWithFormat?.content ?? null,
                reviewerRemarkHTML: value?.commentWithFormat?.contentHtml ?? null,
                committeeInfo:
                    committeeInfo && (committeeInfo[0]?.committeeID || committeeInfo[0]?.projectRoleID)
                        ? committeeInfo
                        : [],
                reviewerUserID: reviewerUserID,
                isDraft: isDraft,
            }
        }
        return param
    }

    const processSaveData = async (
        param: REQServiceType.SpecifyProjectTypeRequestType,
        changeTabOpt?: { idx: number; selectedInfo: any },
    ) => {
        dialogRef.current?.closeDialog()
        try {
            loadingHook.loadingOpen()

            let response
            if (isEditDirector.current) {
                const paramForSave = { ...param } as REQServiceType.UpdSubmittedCommitteeType
                response = await callWebService.updSubmittedCommittee(paramForSave)
            } else {
                const paramForSave = { ...param } as REQServiceType.SpecifyProjectTypeRequestType
                response = await callWebService.specifyProjectType(paramForSave)
            }

            if (changeTabOpt) {
                if (changeTabOpt.idx !== null && changeTabOpt.selectedInfo !== null) {
                    tabAndFolderHook.setTab(changeTabOpt.idx, changeTabOpt.selectedInfo)
                }
            } else {
                if (response && response.status) {
                    if (onSaveComplete) onSaveComplete(response.status, param?.isDraft as boolean)
                }
            }
        } catch (ex) {
            console.log('useDefineProject.ts => processSaveData : ', ex)
        } finally {
            loadingHook.loadingClose()
        }
    }

    const handleChangProjectType = (val: defineTypeFormTypes.OptType) => {
        if (parseInt(val?.value) === constConsiderType.exempt) {
            setRequired(false)
        } else {
            setRequired(true)
        }
        formRef.current?.form.restart()
    }

    const handleDirtyChange = (props: any) => {
        let isDirty: boolean = false
        let newFormValue = _.omit(props.values, ['submitType'])
        let initValue = _.omit(initialValues, ['submitType'])

        const isEqual = _.isEqual(initValue, newFormValue)

        if (!isViewMode) {
            if (isEqual) {
                isDirty = false
            } else {
                isDirty = true
            }
        }

        const paramsDirty = {
            tabDirty: isDirty,
            callbackDirty: isDirty ? callbackDirtyProcess : undefined,
        }

        appFolderAndTabAction.setDirty(paramsDirty)
    }

    const callbackDirtyProcess = (tabIndex: number, newTabSelected: any) => {
        const isDraft = true
        const param: REQServiceType.SpecifyProjectTypeRequestType = getSaveServiceParameter(isDraft)
        processSaveData(param, {
            idx: tabIndex,
            selectedInfo: newTabSelected,
        })
    }

    const handleClickBackToSearch = () => {
        history.push(RoutePath.manageProject)
    }

    const handleSubmitForm = (value: any) => {
        if (projectData?.projectID && projectData?.submittedID) {
            let isReviewer = value?.projectType?.value === constConsiderType.exempt.toString()
            const isDraft: boolean = value?.submitType !== 'save'
            const param: REQServiceType.SpecifyProjectTypeRequestType = getSaveServiceParameter(isDraft)

            if (value?.committeeInfo[0]?.director) {
                //ถ้ามีการกำหนดกรรมการแล้ว
                isReviewer = value?.committeeInfo.find(
                    (e: { director: defineTypeFormTypes.OptType; role: defineTypeFormTypes.OptType }) =>
                        parseInt(e?.role?.value) === constCommitteeProjectRole.principalReviewer,
                )
            }

            if (isDraft) {
                //ถ้า save draft ไม่ต้องมี alert confirm
                processSaveData(param)
            } else {
                const isNotAboutPeople: boolean = value?.isNotAboutPeople ?? false
                if (isReviewer || isNotAboutPeople) {
                    //เข้าเงื่อนไขนี้ก็ต่อเมื่อไม่ได้เลือกกรรมการที่มีหน้าที่เป็นผู้ทบทวนหลัก
                    setConfigDialog({
                        title: translate.get('ALERT.TITLE.NOTIFICATION'),
                        content: translate.get('ALERT.CONTENT.NEED_TO_SAVE'),
                        type: 'alert-confirm',
                        onClickConfirm: () => {
                            processSaveData(param)
                        },
                    })
                } else {
                    setConfigDialog({
                        title: translate.get('ALERT.TITLE.WARNING'),
                        content: translate.get('ALERT.CONTENT.COMMITTEE_REVIEWER_AT_LEAST_1_PERSON'),
                        type: 'alert-message',
                    })
                }

                dialogRef.current?.openDialog()
            }
        }
    }

    return {
        formRef,
        configDialog,
        isViewMode: isViewMode,
        initialValues,
        required,
        isShowUpdateDate,
        isEditDirector: isEditDirector.current,
        onRefDialog,
        onChangProjectType: handleChangProjectType,
        onDirtyChange: handleDirtyChange,
        onSubmitForm: handleSubmitForm,
        onClickBackToSearch: handleClickBackToSearch,
    }
}

export default useDefineProject
